import React, {useEffect, useRef} from 'react';
import Button from '../button';
import PropTypes from 'prop-types';
import {useHotkeys} from 'react-hotkeys-hook';
import I18nMessage from '../I18nMessage';
import {markEscapeHandled} from '../../helpers/escapeGuard';
import {SELECT_CLASS_PREFIX} from '../react-select/prefix';

// A biblioteca de atalhos ignora as teclas enquanto um campo tem o foco; salvar e voltar precisam valer ai.
const FIELD_SHORTCUTS = {enableOnTags: ['INPUT', 'TEXTAREA', 'SELECT']};

const OPEN_POPUP = [
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
    '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
    '.react-datepicker-popper',
    `.${SELECT_CLASS_PREFIX}__menu`,
].join(', ');

// Dois F8 no mesmo instante chegam antes de o formulario mostrar que esta gravando.
const SUBMIT_INTERVAL_MS = 500;

/**
 * Rodape de formulario com Voltar [ESC] e Salvar [F8].
 *
 * BUSINESS RULE: os dois atalhos valem com um campo em foco e nao agem enquanto a gravacao esta em andamento.
 * O Esc de uma lista suspensa aberta (select, calendario) e dela: fecha a lista e nao volta do formulario.
 *
 * CONSTRAINT: todo Esc visto aqui e marcado como tratado, para que a janela que contem o formulario nao feche tambem
 * na tecla solta (ver `escapeGuard`).
 */
const GroupButtonForm = (props) => {
    const {submit, cancel, loading} = props;

    const lastSubmit = useRef(0);
    const listOpenAtKeydown = useRef(false);

    // O seletor e o calendario fecham a lista no proprio Esc, antes de o atalho olhar; por isso a lista e vista na
    // fase de captura, antes de qualquer tratamento da tecla.
    useEffect(() => {
        const record = (event) => {
            if (event.key === 'Escape') {
                listOpenAtKeydown.current = !!document.querySelector(OPEN_POPUP);
            }
        };
        document.addEventListener('keydown', record, true);
        return () => document.removeEventListener('keydown', record, true);
    }, []);

    useHotkeys('F8', (event) => {
        const now = Date.now();
        if (loading || event?.repeat || now - lastSubmit.current < SUBMIT_INTERVAL_MS) {
            return;
        }
        lastSubmit.current = now;
        submit();
    }, FIELD_SHORTCUTS, [submit, loading]);

    useHotkeys('ESC', () => {
        if (!cancel) {
            console.log('not implemented');
            return;
        }
        if (loading || listOpenAtKeydown.current || document.querySelector(OPEN_POPUP)) {
            markEscapeHandled();
            return;
        }
        markEscapeHandled();
        cancel();
    }, FIELD_SHORTCUTS, [cancel, loading]);

    return (
        <>
            <div className={'rounded-group-button-form-bottom'}>
                {props.cancel && <Button
                    color={'warning'}
                    disabled={props.loading}
                    className={'outline'}
                    size={'lg'}
                    onClick={props.cancel}>
                    <I18nMessage id="sharedBack" /> [ESC]
                </Button> }

                <Button
                    color={'success'}
                    disabled={props.loading || props.submitDisabled}
                    type="submit"
                    size={'lg'}>
                    <I18nMessage id={props.edit ? 'sharedUpdate' : 'sharedSave'} /> [F8]
                </Button>
            </div>
        </>
    );
};

GroupButtonForm.propTypes = {
    loading: PropTypes.bool,
    submitDisabled: PropTypes.bool,
    cancel: PropTypes.func,
};

export {
    GroupButtonForm,
};
